// src/components/Navig/NavigHeader/headerCardsContainer.jsx
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import throttle from 'lodash/throttle';

import APortalRestrict from '../../APortalRestrict';
import { Avatar } from '../../UI/Avatar/Avatar.js';
import { isJSON } from '../../Utils/QueryBuilderUtils.js';
import {
  getDateTicks,
  getDateTicksModeFromOptions,
  getOptionsForField,
  getObjectDefField,
} from '../../Utils/Utils.js';
import { NavigHorizontalSlide } from './NavigHorizontalSlider.js';
import { useScales } from '../NavigTree/useScale.js';

import {
  IconMask,
  StyledHeaderTop,
  StyledHeaderTopContent,
  StyleHeaderTopContainer,
  StyledStatsPadStyle,
  StatsPadContent,
} from './NavigHeaderCss.js';

import ObjectActionCreators from '../../../actions';
import {
  HEADER_LEVEL_OFFSET,
  NODE_NAVIG_WIDTH,
} from '../../../constants/layout.js';
import { getObjectTypeByLabel } from '../../Utils/NodeUtils.js';

const CORRECTION_POS_X = -5;

/* Copie 1:1 */
const StatsPad = ({ subMap, color }) => {
  let res = [];
  if (subMap?.keys()) {
    for (const key of subMap.keys()) {
      const val = subMap.get(key);
      if (!isNaN(val)) {
        res.push(
          <StyledStatsPadStyle key={`stat-${key}`}>
            {key}: <span style={{ color }}>{val}</span>
          </StyledStatsPadStyle>
        );
      }
    }
    res = <StatsPadContent>{res}</StatsPadContent>;
  }
  return res;
};

/**
 * HeaderCardsContainer
 * Props :
 *  - level (number)
 *  - width (number)
 *  - renderLabel? ({ item, defaultLabel, index }) => ReactNode
 */
const HeaderCardsContainer = React.memo(function HeaderCardsContainer({
  level,
  width,
  x,
  y,
  renderLabel,
}) {
  const dispatch = useDispatch();

  // ---- store minimal + objectsDefList ----
  const {
    displayModeArr,
    panels,
    dicoNameRef,
    statsMap,
    dragged_over,
    activeBand,
    objectsDefList,
    filters,
  } = useSelector(
    (state) => ({
      displayModeArr: state.objects?.displayMode ?? [],
      panels: state.objects?.panels ?? [],
      dicoNameRef: state.objects?.dicoNameRef ?? {},
      statsMap: state.objects?.statsMap ?? new Map(),
      dragged_over: state.objects?.dragged_over,
      activeBand: state.objects?.activeBand,
      objectsDefList: state.objects?.objectsDefList ?? [],
      filters: state.objects?.filters,
    }),
    shallowEqual
  );

  // ---- scales & classifs via useScales() ----
  const { scalesConfig } = useScales(); // bandValues[], colorScales[], bandScales[], arrayClassifBy[]
  const arrayClassifRaw = scalesConfig?.ticks?.[level] ?? [];
  const ticks = scalesConfig?.ticks?.[level] ?? [];
  const colorBandRaw = scalesConfig?.colorScales?.[level];
  const scalerXBand = scalesConfig?.bandScales?.[level];
  const fieldName = scalesConfig?.arrayClassifBy?.[level]; // nom du champ
  const objectType = filters?.[`level${level}`];

  // ---- displayMode calculé ----
  const displayMode = useMemo(() => {
    const mode = displayModeArr[level];
    const pw = panels[level]?.width;
    if (typeof pw === 'number' && pw < 300) return 'stack';
    return mode ?? 'card';
  }, [displayModeArr, panels, level]);

  // ---- état local hover ----
  const [showFilterId, setShowFilterId] = useState(null);
  const anchorRef = useRef(null);
  const handleHideThrottled = useMemo(
    () => throttle(() => setShowFilterId(null), 2500),
    []
  );
  useEffect(() => () => handleHideThrottled.cancel?.(), [handleHideThrottled]);

  // ---- Récupération defField / fieldOptions (comme ton amont) ----
  const { defField, fieldOptions } = useMemo(() => {
    if (!objectType || !fieldName)
      return { defField: undefined, fieldOptions: undefined };

    let df = getObjectDefField(objectsDefList, objectType, fieldName);
    let fo = getOptionsForField(objectsDefList, objectType, fieldName);

    // formula parentValue
    if (df && df.type === 'formula' && df.formulaType === 'parentValue') {
      const objectTypeTrg = df.formulaObject;
      df = getObjectDefField(objectsDefList, objectTypeTrg, df.formulaField);
      fo = getOptionsForField(objectsDefList, objectTypeTrg, df?.attrName);
    }

    return { defField: df, fieldOptions: fo };
  }, [objectsDefList, objectType, fieldName]);

  const fieldType = defField?.type ?? 'text';

  // ---- Normalisation des options (list + mode de ticks) ----
  // const listOptions = useMemo(() => {
  //   if (Array.isArray(fieldOptions)) return fieldOptions;
  //   if (Array.isArray(defField?.options)) return defField.options;
  //   if (typeof defField?.options === 'string') {
  //     try {
  //       const parsed = JSON.parse(defField.options);
  //       return Array.isArray(parsed) ? parsed : [];
  //     } catch {
  //       return [];
  //     }
  //   }
  //   return [];
  // }, [fieldOptions, defField]);

  // pour getDateTicksModeFromOptions (options?.includes('week'/'month'))
  const optionsForTickMode = useMemo(() => {
    if (typeof fieldOptions === 'string') return fieldOptions;
    if (Array.isArray(fieldOptions)) {
      try {
        const bag = fieldOptions
          .map((o) => (typeof o === 'string' ? o : o?.value ?? o?.label ?? ''))
          .filter(Boolean)
          .join(',');
        return bag;
      } catch {
        return '';
      }
    }
    if (typeof defField?.options === 'string') return defField.options;
    return '';
  }, [fieldOptions, defField]);

  // pour l’affichage "monthselector" (comme ton code d’origine)
  // const isMonthSelector =
  //   typeof fieldOptions === 'string' && fieldOptions === 'monthselector';

  // ----------------------------------------------------------------------------
  // ALIGNEMENT "bandes"
  // ----------------------------------------------------------------------------

  // 1) removeDuplicates
  const removeDuplicates = (arr) =>
    Array.isArray(arr)
      ? arr.filter((it, i, self) => self.indexOf(it) === i)
      : [];

  // 2) ticksMode (options → week|month|…)
  const ticksMode = useMemo(
    () => getDateTicksModeFromOptions(optionsForTickMode),
    [optionsForTickMode]
  );

  // 3) arrayClassifTrg (source des bandes)
  const arrayClassifTrg = useMemo(() => {
    if (fieldType === 'date') {
      // comme code 1: getDateTicks(availableSpace, 80, scalerXBand, ticksMode)
      const ticks =
        scalerXBand.ticks(Math.floor((width - NODE_NAVIG_WIDTH) / 40)) || [];

      // const ticks = getDateTicks(width, 100, scalerXBand, ticksMode) || [];
      return ticks;
    }
    // non-date: dedup + garder l’ordre
    return removeDuplicates(arrayClassifRaw);
  }, [fieldType, arrayClassifRaw, scalerXBand, ticksMode, width]);

  // 4) bandWidth (en pixels, basé sur availableSpace = width)
  const bandWidthPx = useMemo(() => {
    if (!arrayClassifTrg || arrayClassifTrg.length <= 1) return 0;

    if (fieldType === 'date') {
      // même logique: largeur totale / nb de bandes
      return width / arrayClassifTrg.length;
    }

    // si scale a step(): (step% * width)
    if (scalerXBand && typeof scalerXBand.step === 'function') {
      return (scalerXBand.step() / 100) * width;
    }

    // sinon si scale a bandwidth(): (bandwidth% * width)
    if (scalerXBand && typeof scalerXBand.bandwidth === 'function') {
      return (scalerXBand.bandwidth() / 100) * width;
    }

    // fallback: diff entre 2 premières positions (en %), puis * width
    try {
      if (arrayClassifTrg.length > 1 && typeof scalerXBand === 'function') {
        const p0 = scalerXBand(arrayClassifTrg[0]) ?? 0;
        const p1 = scalerXBand(arrayClassifTrg[1]) ?? p0;
        return ((p1 - p0) / 100) * width;
      }
    } catch {}
    return 0;
  }, [arrayClassifTrg, fieldType, scalerXBand, width]);

  // 5) étiquettes dates (identique à ton code)
  // const arrayClassifDate = useMemo(() => {
  //   if (fieldType !== 'date') return null;

  //   let lastYear = null;
  //   const MONTH_SHORT = [
  //     'Jan',
  //     'Feb',
  //     'Mar',
  //     'Apr',
  //     'May',
  //     'Jun',
  //     'Jul',
  //     'Aug',
  //     'Sep',
  //     'Oct',
  //     'Nov',
  //     'Dec',
  //   ];

  //   return arrayClassifTrg.map((item) => {
  //     const d = new Date(parseInt(item));
  //     const dd = d.getDate();
  //     const mm = isMonthSelector
  //       ? MONTH_SHORT[d.getMonth()]
  //       : `/${d.getMonth() + 1}`;
  //     const yy = (isMonthSelector ? '' : '/') + (d.getFullYear() % 100);

  //     let out = '';
  //     if (!isMonthSelector) out += dd;
  //     out += mm;
  //     if (yy !== lastYear) out += yy;
  //     lastYear = yy;
  //     return out;
  //   });
  // }, [fieldType, arrayClassifTrg, isMonthSelector]);

  // Couleur de bande (inchangé)
  // const colorBand = useMemo(() => {
  //   if (typeof colorBandRaw === 'function') return colorBandRaw;
  //   return () => 'transparent';
  // }, [colorBandRaw]); // ----------------------------------------------------------------------------

  // ---- action redux (filtre) ----
  const addFieldRestriction = (
    field,
    value,
    objectType_,
    fieldType_,
    comparator,
    level_
  ) =>
    dispatch(
      ObjectActionCreators.addFieldRestriction(
        field,
        value,
        objectType_,
        fieldType_,
        comparator,
        level_
      )
    );

  // ---- rendu ----
  if (displayMode !== 'card' && displayMode !== 'stackcard') return null;
  if (!arrayClassifTrg || arrayClassifTrg.length === 0) {
    return <StyleHeaderTopContainer />;
  }

  return (
    <StyleHeaderTopContainer
      style={{
        position: 'absolute',
        left: panels[level]?.start,
        top: y,
        border: '0px solid blue',
        width: width,
      }}
    >
      {ticks.map((tickObject, ind) => {
        console.log('type of tickObject : %s', typeof tickObject);
        let valToDisp = tickObject == null ? '' : String(tickObject);
        // let iconToUse;
        // let colorToUse;

        if (fieldType === 'date') {
          // console.log(`------${tickObject.display}`);
          // valToDisp = tickObject.display;
        } else if (!['number', 'text', 'formula'].includes(fieldType)) {
          if (dicoNameRef?.[valToDisp] !== undefined) {
            valToDisp = dicoNameRef[valToDisp];
          }
          valToDisp = valToDisp?.substring(0, 15);
        }

        if (fieldType === 'formula' && isJSON(valToDisp)) {
          valToDisp = `calc-${ind}`;
        }

        // if (fieldType === 'list' && Array.isArray(listOptions)) {
        //   const found = listOptions.find((opt) => opt.value === tickObject);
        //   if (found) {
        //     valToDisp = tickObject.display ?? valToDisp;
        //     iconToUse = found.icon;
        //     colorToUse = found.color || '#333333';
        //   }
        // }

        const id = `inDiv${level}${ind}`;
        const isHover =
          dragged_over === tickObject || activeBand === tickObject;
        const posX =
          Math.floor((scalerXBand(tickObject?.raw) / 100) * width) +
          CORRECTION_POS_X;

        return (
          <StyledHeaderTop
            key={ind}
            className="navigHeader"
            ref={anchorRef}
            $color={tickObject.color || 'var(--ds-text-base)'}
            $posX={posX}
            $level={level}
            $width={bandWidthPx}
            $hover={isHover}
            $noneHover={!(dragged_over || activeBand)}
            id={id}
            onMouseOver={(e) => setShowFilterId(e.currentTarget.id)}
            onMouseLeave={handleHideThrottled}
          >
            {showFilterId === id && (
              <APortalRestrict
                idParent={id}
                aref={anchorRef}
                addFieldRestriction={(field, value, comparator) =>
                  addFieldRestriction(
                    field,
                    value,
                    objectType,
                    fieldType,
                    comparator,
                    level
                  )
                }
                level={level}
                fieldType={fieldType}
                type={fieldType}
                item={tickObject}
                objectType={objectType}
              >
                <StatsPad
                  subMap={statsMap?.get(tickObject)}
                  color={'var(--ds-text-secondary)'}
                />
              </APortalRestrict>
            )}

            <StyledHeaderTopContent $color={tickObject.color}>
              {typeof tickObject === 'object' &&
                String(tickObject?.raw || '').includes('emplo') && (
                  <Avatar
                    objId={tickObject.raw}
                    ratio={0.6}
                    marginTop={0}
                    marginLeft={-2}
                  />
                )}
              {tickObject.icon && (
                <IconMask
                  key={`icon-hd-${tickObject.icon}-${tickObject.color}`}
                  $color={tickObject.color}
                  $icon={tickObject.icon}
                  className="iconMask"
                />
              )}
              {renderLabel
                ? renderLabel({
                    item: tickObject.raw,
                    defaultLabel: `${valToDisp}`.toLowerCase(),
                    index: ind,
                  })
                : `${tickObject.display}`.toLowerCase()}
            </StyledHeaderTopContent>
          </StyledHeaderTop>
        );
      })}

      {fieldType === 'date' && (
        <NavigHorizontalSlide width={width} level={level} />
      )}
    </StyleHeaderTopContainer>
  );
});

export default HeaderCardsContainer;
